import { useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    UploadCloud,
    FileUp,
    Sparkles,
    Loader2,
    CheckCircle2,
    AlertCircle,
    X,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_STYLE = {
    queued: "border-ink/30 text-ink-soft bg-white",
    analyzing: "border-accent text-accent bg-accent/5",
    success: "border-ok text-ok bg-ok/5",
    error: "border-danger text-danger bg-danger/5",
};

const STATUS_LABEL = {
    queued: "En attente",
    analyzing: "Analyse en cours…",
    success: "Analysé",
    error: "Erreur",
};

export default function BulkUploadDialog({ projectId, onUploaded, trigger }) {
    const [open, setOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [model, setModel] = useState("claude");
    const [running, setRunning] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setFiles([]);
        setRunning(false);
    };

    const addFiles = (filelist) => {
        const incoming = Array.from(filelist);
        const accepted = incoming.filter(
            (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
        );
        const rejected = incoming.length - accepted.length;
        if (rejected > 0) {
            toast.error(
                `${rejected} fichier(s) ignoré(s) : seuls les PDF sont acceptés.`
            );
        }
        setFiles((prev) => [
            ...prev,
            ...accepted.map((f) => ({
                id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
                file: f,
                status: "queued",
                result: null,
                error: null,
            })),
        ]);
    };

    const removeFile = (id) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    };

    const runAll = async () => {
        const queued = files.filter((f) => f.status === "queued" || f.status === "error");
        if (queued.length === 0) {
            toast.error("Aucun fichier à analyser.");
            return;
        }
        setRunning(true);
        let okCount = 0;
        let errCount = 0;
        for (const item of queued) {
            // mark analyzing
            setFiles((prev) =>
                prev.map((f) =>
                    f.id === item.id ? { ...f, status: "analyzing", error: null } : f
                )
            );
            try {
                const devis = await api.uploadDevis(projectId, item.file, model);
                okCount += 1;
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? { ...f, status: "success", result: devis }
                            : f
                    )
                );
            } catch (e) {
                errCount += 1;
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? {
                                  ...f,
                                  status: "error",
                                  error:
                                      e?.response?.data?.detail ||
                                      e?.message ||
                                      "Erreur d'analyse",
                              }
                            : f
                    )
                );
            }
        }
        setRunning(false);
        if (okCount > 0) toast.success(`${okCount} devis analysé(s) avec succès`);
        if (errCount > 0) toast.error(`${errCount} échec(s)`);
        onUploaded && onUploaded();
    };

    const close = () => {
        if (running) return;
        setOpen(false);
        reset();
    };

    const totalQueued = files.filter((f) => f.status === "queued").length;
    const totalSuccess = files.filter((f) => f.status === "success").length;
    const totalError = files.filter((f) => f.status === "error").length;
    const totalAnalyzing = files.filter((f) => f.status === "analyzing").length;
    const progress =
        files.length === 0
            ? 0
            : Math.round(((totalSuccess + totalError) / files.length) * 100);

    return (
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button
                        data-testid="bulk-upload-btn"
                        variant="outline"
                        className="rounded-none border-accent text-accent hover:bg-accent hover:text-white"
                    >
                        <UploadCloud className="w-4 h-4 mr-2" strokeWidth={1.6} />
                        Import groupé
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <UploadCloud
                            className="w-5 h-5 text-accent"
                            strokeWidth={1.6}
                        />
                        Import groupé de devis PDF
                    </DialogTitle>
                </DialogHeader>

                <div className="text-xs text-ink-soft border-l-2 border-accent pl-3">
                    Sélectionnez plusieurs PDF d'un coup. L'app les analysera
                    l'un après l'autre (15–45 s par devis). Vous pouvez fermer
                    cette fenêtre uniquement quand le traitement est terminé.
                </div>

                <div>
                    <Label className="stat-label">Modèle d'analyse</Label>
                    <Select
                        value={model}
                        onValueChange={setModel}
                        disabled={running}
                    >
                        <SelectTrigger
                            data-testid="bulk-model-select"
                            className="rounded-none mt-1.5"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                            <SelectItem value="claude">
                                Claude Sonnet 4.5 (recommandé)
                            </SelectItem>
                            <SelectItem value="openai">GPT-5.2</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => !running && inputRef.current?.click()}
                    data-testid="bulk-dropzone"
                    className={`border-2 border-dashed border-ink/30 ${
                        !running && "hover:border-accent cursor-pointer"
                    } transition-colors p-6 text-center bg-[#FAFAFA]`}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept="application/pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            if (e.target.files?.length) addFiles(e.target.files);
                            e.target.value = "";
                        }}
                        data-testid="bulk-file-input"
                    />
                    <FileUp
                        className="w-8 h-8 mx-auto mb-2 text-ink-soft"
                        strokeWidth={1.5}
                    />
                    <div className="font-medium text-ink">
                        Glissez-déposez plusieurs PDF
                    </div>
                    <div className="text-xs text-ink-soft mt-1">
                        ou cliquez pour les sélectionner
                    </div>
                </div>

                {files.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="stat-label">
                                {files.length} fichier
                                {files.length > 1 ? "s" : ""} ·{" "}
                                {totalSuccess} ok · {totalError} erreur
                                {totalError > 1 ? "s" : ""} · {totalQueued} en
                                attente
                            </Label>
                            {!running && (
                                <button
                                    onClick={reset}
                                    className="text-[11px] uppercase tracking-wide text-ink-soft hover:text-danger inline-flex items-center gap-1"
                                    data-testid="bulk-clear"
                                >
                                    <Trash2 className="w-3 h-3" />
                                    Tout retirer
                                </button>
                            )}
                        </div>

                        {/* progress bar */}
                        <div className="h-1.5 w-full bg-[#F0F0F0] mb-3">
                            <div
                                className="h-full bg-accent transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>

                        <div className="border border-border max-h-[260px] overflow-y-auto">
                            {files.map((f) => (
                                <div
                                    key={f.id}
                                    className="px-3 py-2.5 border-b border-border last:border-0 flex items-center gap-3 hover:bg-[#FAFAFA]"
                                    data-testid={`bulk-row-${f.id}`}
                                >
                                    <div className="shrink-0">
                                        {f.status === "queued" && (
                                            <FileUp
                                                className="w-4 h-4 text-ink-soft"
                                                strokeWidth={1.6}
                                            />
                                        )}
                                        {f.status === "analyzing" && (
                                            <Loader2 className="w-4 h-4 animate-spin text-accent" />
                                        )}
                                        {f.status === "success" && (
                                            <CheckCircle2
                                                className="w-4 h-4 text-ok"
                                                strokeWidth={2}
                                            />
                                        )}
                                        {f.status === "error" && (
                                            <AlertCircle
                                                className="w-4 h-4 text-danger"
                                                strokeWidth={2}
                                            />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium truncate">
                                            {f.file.name}
                                        </div>
                                        <div className="text-[11px] text-ink-soft">
                                            {(f.file.size / 1024).toFixed(0)} Ko
                                            {f.status === "success" && f.result && (
                                                <>
                                                    {" · "}
                                                    <b>{f.result.contractor_name}</b>{" "}
                                                    · {f.result.line_items?.length || 0}{" "}
                                                    postes
                                                </>
                                            )}
                                            {f.status === "error" && f.error && (
                                                <> · {String(f.error).slice(0, 100)}</>
                                            )}
                                        </div>
                                    </div>
                                    <span
                                        className={`pill ${
                                            STATUS_STYLE[f.status]
                                        }`}
                                    >
                                        {STATUS_LABEL[f.status]}
                                    </span>
                                    {!running && f.status !== "analyzing" && (
                                        <button
                                            onClick={() => removeFile(f.id)}
                                            className="text-ink-soft hover:text-danger p-1"
                                            data-testid={`bulk-remove-${f.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={close}
                        disabled={running}
                    >
                        Fermer
                    </Button>
                    <Button
                        data-testid="bulk-run"
                        className="rounded-none bg-accent hover:bg-accent-hover"
                        onClick={runAll}
                        disabled={
                            running ||
                            files.filter(
                                (f) => f.status === "queued" || f.status === "error"
                            ).length === 0
                        }
                    >
                        {running ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyse {totalAnalyzing > 0 ? "en cours…" : ""}
                            </>
                        ) : (
                            <>
                                <Sparkles
                                    className="w-4 h-4 mr-2"
                                    strokeWidth={1.6}
                                />
                                Lancer l'analyse ({" "}
                                {files.filter(
                                    (f) =>
                                        f.status === "queued" ||
                                        f.status === "error"
                                ).length}{" "}
                                )
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
